/**
 * Транспорт поста — второй релиз партии 19.09.2026 (docs/release-2026-09-19.md §9): отправка в
 * тестовый канал, PDF внизу поста, политика «все посты — расширенным сообщением».
 *
 * Провод между бэкендом (`domain/test-send.ts`, `domain/transport-policy.ts`,
 * `domain/document.ts`) и интерфейсом. Подписываемые формы — ключ `preview` и дескриптор
 * документа — живут не здесь, а в `approval-key/` и `telegram/draft.ts`.
 */

import * as z from 'zod';
import { BRAND_RE, CHAT_ID_RE, CONTENT_HASH_RE } from '../approval-key/limits.ts';
import { UUID_V7_RE } from '../ids.ts';
import { HUMAN_DOCUMENT_MAX_BYTES } from '../media.ts';
import { ISO_UTC_MS } from '../time.ts';

const Uuid = z.string().regex(UUID_V7_RE);
const IsoUtcMs = z.string().regex(ISO_UTC_MS);

// ─────────────────────────────────────────────────────────────────────────────
// GET/POST /api/v1/transport-policy — «Все посты — расширенным сообщением» (§9.3)

/**
 * Политика бренда. Строки в `transport_policy` нет — политика ВКЛЮЧЕНА (`source: 'default'`):
 * так решено для выкладки (просьба Ильи 19.09.2026). Политика действует на версии, собранные
 * ПОСЛЕ переключения: уже замороженная версия уходит тем способом, который в неё подписан.
 */
export const TransportPolicySchema = z.strictObject({
  brand: z.string().regex(BRAND_RE),
  rich_always: z.boolean(),
  /** `default` — строки нет, действует умолчание; `set` — решение человека. */
  source: z.enum(['default', 'set']),
  note: z.string().nullable(),
  changed_by_login: z.string().nullable(),
  updated_at: IsoUtcMs.nullable(),
});
export type TransportPolicy = z.output<typeof TransportPolicySchema>;

export const SetTransportPolicyBodySchema = z.strictObject({
  brand: z.string().regex(BRAND_RE),
  rich_always: z.boolean(),
  note: z.string().min(1).max(500).nullable().default(null),
});
export type SetTransportPolicyBody = z.output<typeof SetTransportPolicyBodySchema>;

/** Политика по умолчанию — одна на весь код: бэкенд без строки и интерфейс до ответа. */
export const TRANSPORT_RICH_ALWAYS_DEFAULT = true;

/**
 * `GET /intake/v1/transport-policy?brand=` — та же политика глазами сервиса генерации. Писатель
 * замораживает черновик локально и сверяет свой `content_hash` с ответом бэкенда
 * (`agent-writer/src/pipeline/submit.ts`, `hash_mismatch`): заморозь он без политики, каждый
 * короткий пост при включённой политике давал бы расхождение и останавливал прогон. Поэтому
 * писатель читает политику на открытии прогона, как модель и стиль. Скоуп — `drafts:write`:
 * политика — часть того, как пишется черновик, а не знание о бренде.
 */
export const IntakeTransportPolicySchema = z.strictObject({
  brand: z.string().regex(BRAND_RE),
  rich_always: z.boolean(),
});
export type IntakeTransportPolicy = z.output<typeof IntakeTransportPolicySchema>;

// ─────────────────────────────────────────────────────────────────────────────
// POST /api/v1/drafts/:id/versions/:n/test-send — «Запостить в тестовый канал» (§9.1)

/**
 * Тело запроса. `expected_content_hash` — та же защита, что у одобрения: человек отправляет
 * ровно ту версию, которую видит, и правка в соседней вкладке не уедет в канал под его именем.
 *
 * `expected_brand` ОБЯЗАТЕЛЕН (2026-10-25, изоляция брендов): бренд черновика на экране.
 * Канала в теле нет — превью уходит только в тестовый канал бренда черновика, и выбора у
 * человека нет; расхождение бренда — 409 `brand_mismatch` (шапка `decisions.ts`).
 */
export const CreateTestSendBodySchema = z.strictObject({
  expected_content_hash: z.string().regex(CONTENT_HASH_RE),
  expected_brand: z.string().regex(BRAND_RE),
});
export type CreateTestSendBody = z.output<typeof CreateTestSendBodySchema>;

export const TEST_SEND_STATES = ['signed', 'leased', 'sent', 'failed', 'refused'] as const;
export type TestSendState = (typeof TEST_SEND_STATES)[number];

/**
 * Строка отправки в тестовый канал. Жизнь: `signed` (ключ выдан) → `leased` (выдана
 * публикатору) → `sent` | `failed` | `refused`. Незакрытая строка дольше 10 минут
 * закрывается уборкой как `failed` («публикатор не отчитался»).
 */
export const TestSendSchema = z.strictObject({
  id: Uuid,
  draft_id: Uuid,
  version_no: z.int().positive(),
  content_hash: z.string().regex(CONTENT_HASH_RE),
  /** Тестовый канал бренда в момент отправки — для ссылки и подписи на экране. */
  chat_id: z.string().regex(CHAT_ID_RE),
  state: z.enum(TEST_SEND_STATES),
  telegram_message_id: z.int().positive().nullable(),
  /** `https://t.me/c/<id без -100>/<message_id>` — только у `sent`. */
  link: z.string().nullable(),
  /** Код исхода словами публикатора или уборки: `PREVIEW_TARGET_NOT_TEST`, `lease_expired`… */
  outcome_code: z.string().nullable(),
  /** По-русски, для подсказки на экране. */
  outcome_detail: z.string().nullable(),
  requested_by_login: z.string().nullable(),
  created_at: IsoUtcMs,
  finished_at: IsoUtcMs.nullable(),
});
export type TestSend = z.output<typeof TestSendSchema>;

/**
 * Ответ на создание: 201 — новая отправка; 200 — у версии уже есть незакрытая отправка, и
 * второе нажатие получает ЕЁ, а не вторую копию поста в тестовом канале (частичный UNIQUE
 * `ux_test_send_active`, миграция 0020).
 */
export const CreateTestSendResponseSchema = z.strictObject({
  test_send: TestSendSchema,
  created: z.boolean(),
});
export type CreateTestSendResponse = z.output<typeof CreateTestSendResponseSchema>;

// ─────────────────────────────────────────────────────────────────────────────
// POST /api/v1/drafts/:id/versions/:n/document — PDF внизу поста (§9.2)

/**
 * Загрузка документа создаёт НОВУЮ версию черновика (как правка человека): документ входит в
 * подписываемое содержание, и версия, которую человек утвердил без файла, не может получить
 * его задним числом. Снятие документа — тоже новая версия.
 */
export const DocumentUploadResponseSchema = z.strictObject({
  media_id: Uuid,
  sha256: z.string().regex(/^[0-9a-f]{64}$/),
  bytes: z.int().positive().max(HUMAN_DOCUMENT_MAX_BYTES),
  file_name: z.string().min(1),
  /** Номер новой версии черновика — уже с документом. */
  version_no: z.int().positive(),
  content_hash: z.string().regex(CONTENT_HASH_RE),
});
export type DocumentUploadResponse = z.output<typeof DocumentUploadResponseSchema>;
